import { Intent } from '@applyboard/ui-icons';
import { Theme } from '@emotion/react';
import React, { ReactNode, useContext } from 'react';
import {
  GradientColorToken,
  useFullTheme,
  MarginProps,
  GradientDirection,
  Responsive,
  gradientDirectionMap,
} from '~/design';
import { CardContext, useLinkAs, BackgroundContext, BackgroundProvider } from '~/internal';
import { findChildOfType, useGradient, isChildType, useResponsive } from '~/utils';

export interface CardProps {
  /** Use this prop to provide an accessible name to the card */
  'aria-label'?: string;

  /** Use this instead of aria-label if there's a visual label on the screen that could label the card */
  'aria-labelledby'?: string;

  /**
   * The underlying HTML element to render (default: `section`)
   * - Use `article` for a self-contained composition in a document, page, application, or site, which is intended to be independently distributable or reusable (e.g., in syndication).
   * Examples include: a forum post, a magazine or newspaper article, or a blog entry, a product card, a user-submitted comment, an interactive widget or gadget, or any other independent item of content.
   * - Use `section` for a generic standalone section of a document, which doesn't have a more specific semantic element to represent it. Sections should always have a heading, with very few exceptions.
   */
  as?: 'article' | 'section';

  /** The content of the card */
  children: React.ReactNode;

  /** The intent of the card */
  intent?: Intent;

  /** The emphasis of the card against its background (default: `outlined`) */
  emphasis?:
    | Responsive<'elevated' | 'highlighted' | 'outlined' | 'plain'>
    | 'elevated'
    | 'highlighted'
    | 'outlined'
    | 'plain';

  /** Adds a predefined gradient as the card background color. For accessibility concerns, make sure
   * that the text you'll add to the card has sufficient contrast with the background. */
  gradient?: GradientColorToken;

  /** What will the angle of the gradient background be. */
  gradientDirection?: GradientDirection;

  /** The height of the Card. `height=fill` is handy when you have adjacent cards in
   * Flex.Item that need to align both along their top and bottom borders. */
  height?: 'fill' | 'hug';

  /** Whether or not the card should appear as "selected" (default: false) */
  selected?: boolean;

  /**
   * The display variation of the card (default: `outlined`)
   * @deprecated Use `emphasis` instead.
   */
  variant?: 'elevated' | 'highlighted' | 'outlined' | 'plain';

  /**
   * The padding of the card. Set to 0 to remove all padding.
   */
  p?: 0;
}

/**
 * `Card` is a flexible component used to group and display content with an optional image.
 *
 * ## Accessibility ♿
 * - Be careful when picking the `as` property.  See the documentation for the `as` prop for more information
 * - Use `aria-label` to label the Card.  In the future, one of `aria-label` or `aria-labelledby` may become mandatory. See [UIE-1165](https://applyboard.atlassian.net/browse/UIE-1165)
 * - Be mindful of text contrast when using the `gradient` prop.
 */
export function Card({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  as = 'section',
  children,
  emphasis: _emphasis = 'outlined',
  intent,
  gradient,
  gradientDirection = 'horizontal',
  height = 'hug',
  selected = false,
  variant,
  p,
}: CardProps) {
  const emphasis = variant ?? _emphasis;
  const Tag = as;
  const { animation, borders, borderRadiuses, colors, elevations, spacing } = useFullTheme();
  const responsive = useResponsive();

  const { duration, easing, translateY } = animation || {};

  const linkChild = findChildOfType(children, Card.Linked) as unknown as
    | React.ReactElement<CardLinkedProps>
    | undefined;
  const buttonChild = findChildOfType(children, Card.Clickable) as unknown as
    | React.ReactElement<CardClickableProps>
    | undefined;
  const imageChild = findChildOfType(children, Card.Image);
  const footerChild = findChildOfType(children, Card.Footer);
  const adornmentChild = findChildOfType(children, Card.Adornment);
  const remainingChildren = React.Children.toArray(children).filter(
    child =>
      !isChildType(child, Card.Adornment) &&
      !isChildType(child, Card.Footer) &&
      !isChildType(child, Card.Image) &&
      !isChildType(child, Card.Linked) &&
      !isChildType(child, Card.Clickable),
  );
  const gradientBg = useGradient(gradient);
  const backgroundColor = getBackgroundColor({ selected, gradientBg, intent, colors });

  if (!!buttonChild && !!linkChild) {
    throw new Error('Card.Linked and Card.Clickable cannot be used together.');
  }

  if (ariaLabel && ariaLabelledBy) {
    throw new Error('Only one of aria-label or aria-labelledby can be provided');
  }

  const emphasisStyle = responsive.css({
    borderColor: responsive.map(emphasis, emphasisValue => {
      if (selected) {
        return colors.actionPrimary;
      }
      return emphasisValue === 'outlined' ? borders.regular.borderColor : 'transparent';
    }),
    borderStyle: responsive.map(emphasis, emphasisValue => {
      if (selected) {
        return 'solid';
      }
      return emphasisValue === 'outlined' ? borders.regular.borderStyle : 'solid';
    }),
    borderWidth: responsive.map(emphasis, emphasisValue => {
      if (selected) {
        return spacing['2px'];
      }
      return emphasisValue === 'outlined' ? borders.regular.borderWidth : undefined;
    }),
    background: responsive.map(emphasis, emphasisValue => {
      if (Array.isArray(backgroundColor)) {
        return `linear-gradient(${gradientDirectionMap[gradientDirection]}, ${backgroundColor[0]}, ${backgroundColor[1]})`;
      }

      if (backgroundColor) {
        return backgroundColor;
      }

      return emphasisValue === 'highlighted' ? colors.bgSecondaryHighlight : colors.bgRegular;
    }),
    boxShadow: responsive.map(emphasis, emphasisValue => {
      return emphasisValue === 'elevated' ? elevations.z1 : 'none';
    }),
  });

  const clickableStyle =
    !linkChild && !buttonChild
      ? undefined
      : responsive.css({
          '&:hover': {
            boxShadow: responsive.map(emphasis, emphasisValue => {
              const hoverShadows = {
                elevated: elevations.z3,
                highlighted: elevations.z1,
                outlined: elevations.z1,
                plain: elevations.z1,
              };
              return hoverShadows[emphasisValue];
            }),
            ...(duration &&
              easing &&
              translateY && {
                transform: translateY,
                '@media (prefers-reduced-motion: no-preference)': {
                  transition: `transform ${easing}, box-shadow ${easing}, background-color ${easing}`,
                },
              }),
          },
          '&:active': {
            backgroundColor: responsive.map(emphasis, emphasisValue => {
              const activeBackgrounds = {
                elevated: colors.bgRegularPressed,
                highlighted: colors.bgRegularPressed,
                outlined: colors.bgRegularHover, // Intentionally 'hover'
                plain: colors.bgRegularHover, // Intentionally 'hover'
              };
              return activeBackgrounds[emphasisValue];
            }),
          },
        });

  return (
    <CardContext.Provider
      value={{
        hasFooterChild: !!footerChild,
        hasLinkChild: !!linkChild,
        hasButtonChild: !!buttonChild,
      }}
    >
      <BackgroundProvider backgroundColor={backgroundColor}>
        <div
          css={{
            ...borders.transparent,
            height: height === 'fill' ? '100%' : undefined,
            borderWidth: selected ? spacing[0] : spacing['1px'],
          }}
        >
          <Tag
            aria-current={selected ? true : undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            css={[
              {
                ...borders.transparent,
                borderRadius: borderRadiuses.container,
                display: 'flex',
                flexDirection: 'column',
                height: height === 'fill' ? '100%' : undefined,
              },
              (linkChild || buttonChild || adornmentChild) && {
                position: 'relative',
              },
              emphasisStyle,
              clickableStyle,
            ]}
          >
            {linkChild}

            {buttonChild}

            {imageChild}

            {!!adornmentChild && (
              <div
                css={{
                  position: 'absolute',
                  right: spacing[4],
                  top: `-${spacing[3]}`,
                  zIndex: 1,
                }}
              >
                {adornmentChild}
              </div>
            )}

            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                gap: spacing[2],
                padding: p === 0 ? 0 : spacing[4],
              }}
            >
              {remainingChildren}
            </div>

            {footerChild}
          </Tag>
        </div>
      </BackgroundProvider>
    </CardContext.Provider>
  );
}

export interface CardHeaderProps {
  children: React.ReactNode;
}

Card.Header = function CardHeader({ children }: CardHeaderProps) {
  const context = React.useContext(CardContext);
  if (!context) {
    throw new Error('`Card.Header` can only be rendered inside `Card`');
  }

  return <header>{children}</header>;
};

export interface CardFooterProps {
  children: React.ReactNode;

  emphasis?: 'highlighted' | 'transparent';
}

Card.Footer = function CardFooter({ children, emphasis = 'transparent' }: CardFooterProps) {
  const theme = useFullTheme();
  const context = React.useContext(CardContext);
  if (!context || !context.hasFooterChild) {
    throw new Error('`Card.Footer` can only be rendered inside `Card`');
  }

  return (
    <footer
      css={{
        backgroundColor: emphasis === 'transparent' ? undefined : theme.colors.bgSecondaryHighlight,
        borderBottomLeftRadius: theme.borderRadiuses.container,
        borderBottomRightRadius: theme.borderRadiuses.container,
        padding: theme.spacing[2],
      }}
    >
      {children}
    </footer>
  );
};

export interface CardImageProps {
  /** A text description of the card image. It's required for accessibility */
  alt: string;

  /** Content to overlay on top of the image. */
  children?: React.ReactNode;

  /** The height of the image. */
  height?: number | Responsive<number>;

  /** The position of the image. */
  position?: string;

  /** The path to the card image */
  src: string;

  /** Adds a predefined gradient as the Card.Image. For accessibility concerns, make sure
   * that the text you'll add to the Card.Image has sufficient contrast with the background. */
  gradient?: GradientColorToken;

  /** What will the angle of the gradient background be. */
  gradientDirection?: GradientDirection;
}

Card.Image = function CardImage({
  alt,
  children,
  gradient,
  gradientDirection = 'horizontal',
  height,
  position,
  src,
}: CardImageProps) {
  const backgroundContext = React.useContext(BackgroundContext);

  if (!React.useContext(CardContext)) {
    throw new Error('`Card.Image` can only be rendered inside `Card`');
  }

  if (backgroundContext?.isBackgroundGradient) {
    throw new Error(
      '`Card.Image` cant have `gradient` as background if parent `Card` already has a `gradient` property',
    );
  }

  const { borderRadiuses, spacing } = useFullTheme();
  const borderRadius = `${borderRadiuses.container} ${borderRadiuses.container} 0 0`;
  const responsive = useResponsive();
  const gradientBg = useGradient(gradient);

  return (
    <BackgroundProvider backgroundColor={gradientBg}>
      <div
        css={[
          {
            background: gradientBg
              ? `linear-gradient(${gradientDirectionMap[gradientDirection]}, ${gradientBg[0]}, ${gradientBg[1]})`
              : undefined,
            borderRadius,
            position: children ? 'relative' : undefined,
          },
          responsive.css({ height: height ?? '100%' }),
        ]}
      >
        {src !== '' && (
          <img
            alt={alt}
            css={[
              {
                borderRadius,
                display: 'block',
                objectFit: 'cover',
                objectPosition: position,
                width: '100%',
              },
              responsive.css({ height: height ?? '100%' }),
            ]}
            src={src}
          />
        )}

        {children && (
          <div
            css={{
              height: `calc(100% - (2 * ${spacing[4]}))`,
              left: spacing[4],
              position: 'absolute',
              top: spacing[4],
              width: `calc(100% - (2 * ${spacing[4]}))`,
            }}
          >
            {children}
          </div>
        )}
      </div>
    </BackgroundProvider>
  );
};

export interface CardLinkedProps {
  /** The aria-label of the link. For a screen reader user, this is what will be read. */
  'aria-label': string;

  /** The href of the link. */
  href: string;

  /** A callback function that is invoked when the card is clicked. Useful for event tracking.  */
  onClick?: () => void;

  /** The rel of the link. For security reasons, it is _highly_ recommended to include 'noopener'
   * in this value. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/rel for
   * possible values and formatting. (default: 'noopener') */
  rel?: string;
}

function CardLink({ 'aria-label': ariaLabel, href, onClick, rel = 'noopener' }: CardLinkedProps) {
  const LinkAs = useLinkAs();
  const { borderRadiuses, colors } = useFullTheme();
  const context = React.useContext(CardContext);
  if (!context || !context.hasLinkChild) {
    throw new Error('`Card.Linked` can only be rendered inside `Card`');
  }
  if (!ariaLabel) {
    throw new Error('aria-label is required in Card.Linked');
  }
  if (!rel.includes('noopener')) {
    throw new Error(
      '[Card] Card.Linked was rendered without including "noopener" in the "rel" attribute. This is a potential security vulnerability.',
    );
  }

  return (
    <LinkAs
      aria-label={ariaLabel}
      css={[
        {
          '::after': {
            bottom: 0,
            content: '""',
            cursor: 'pointer',
            left: 0,
            position: 'absolute',
            right: 0,
            top: 0,
            zIndex: 1,
          },
        },
        {
          ':focus-visible::after': {
            outline: `2px solid ${colors.actionPrimary}`,
            outlineOffset: '4px',
            borderRadius: borderRadiuses.container,
          },
        },
      ]}
      href={href}
      onClick={onClick}
      rel={rel}
    >
      {/* Intentionally empty */}
      <></>
    </LinkAs>
  );
}

/**
 * Using Card.Linked will make the Card a link in an accessible manner.
 */
Card.Linked = function CardLinked({
  'aria-label': ariaLabel,
  href,
  onClick,
  rel,
}: CardLinkedProps) {
  return <CardLink aria-label={ariaLabel} href={href} onClick={onClick} rel={rel} />;
};

interface CardClickableProps {
  /** The aria-label of the button. For a screen reader user, this is what will be read. */
  'aria-label': string;

  /** A callback function that is invoked when the card is clicked. */
  onClick?: () => void;
}

function CardButton({ 'aria-label': ariaLabel, onClick }: CardClickableProps) {
  const context = React.useContext(CardContext);
  const { borderRadiuses, colors } = useFullTheme();

  if (!context || !context.hasButtonChild) {
    throw new Error('`Card.Clickable` can only be rendered inside `Card`');
  }
  if (!ariaLabel) {
    throw new Error('aria-label is required in Card.Clickable');
  }

  return (
    <button
      aria-label={ariaLabel}
      css={{
        backgroundColor: 'transparent',
        border: 0,
        padding: 0,
        '::after': {
          bottom: 0,
          content: '""',
          cursor: 'pointer',
          left: 0,
          position: 'absolute',
          right: 0,
          top: 0,
        },
        ':focus-visible::after': {
          outline: `2px solid ${colors.actionPrimary}`,
          outlineOffset: '4px',
          borderRadius: borderRadiuses.container,
        },
      }}
      onClick={onClick}
      type="button"
    >
      {/* Intentionally empty */}
    </button>
  );
}

Card.Clickable = function CardClickable({ 'aria-label': ariaLabel, onClick }: CardClickableProps) {
  return <CardButton aria-label={ariaLabel} onClick={onClick} />;
};

export type CardDividerProps = Pick<MarginProps, 'mb' | 'mt'>;

Card.Divider = function CardDivider({ mb = 0, mt = 0 }: CardDividerProps) {
  if (!React.useContext(CardContext)) {
    throw new Error('`Card.Divider` can only be rendered inside `Card`');
  }

  const { borders, spacing } = useFullTheme();
  const border = borders.regular;

  return (
    <hr
      css={[
        {
          borderWidth: 0,
          borderColor: border.borderColor,
          borderStyle: border.borderStyle,
          borderTopWidth: border.borderWidth,
          marginInline: `-${spacing[4]}`,
          marginBottom: spacing[mb],
          marginTop: spacing[mt],
        },
      ]}
    />
  );
};

export interface CardAdornmentProps {
  /** The adornment content. */
  children: ReactNode;
}

Card.Adornment = function CardAdornment({ children }: CardAdornmentProps) {
  if (!useContext(CardContext)) {
    throw new Error('`Card.Adornment` can only be rendered inside `Card`');
  }
  return <>{children}</>;
};

function getBackgroundColor({
  selected,
  gradientBg,
  intent,
  colors,
}: {
  selected: boolean;
  gradientBg: [string, string] | undefined;
  intent: Intent | undefined;
  colors: Theme['colors'];
}) {
  if (selected) {
    return colors.cardSelected;
  }

  if (gradientBg) {
    return gradientBg;
  }

  if (intent) {
    const mapping: Record<Intent, string> = {
      positive: colors.bgPositiveHighlight,
      secondary: colors.bgSecondaryHighlight,
      magic: colors.bgMagicHighlight,
      primary: colors.bgPrimaryHighlight,
      warning: colors.bgWarningHighlight,
      negative: colors.bgNegativeHighlight,
    };

    return mapping[intent];
  }

  return undefined;
}
